import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import { readWithRetries } from '../api/read-with-retries';
import { useAdministrationScopes } from './AdministrationScopesProvider';
import { apiPlace, placeKey, type Place, type PlaceDetail } from './place';

export type PlaceState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly detail: PlaceDetail }
  /** 404 PLACE_NOT_ADMINISTERED: missing, malformed, or not administered — one answer for all three. */
  | { readonly status: 'not-administered' }
  | { readonly status: 'unavailable' };

/**
 * The place in the address, as the server describes it to this caller: its name, its tenant, and
 * the effective `can` there (the place read). Read once per place per session; a place the
 * caller holds a grant on shows at once from its `/scopes` entry while the read confirms it.
 *
 * The platform has no place read: the global grant's own entry stands for it, and without one the
 * caller does not administer the platform.
 */
export function usePlace(place: Place): { readonly state: PlaceState; readonly retry: () => void } {
  const { scopes, places, placeReads, rememberPlace } = useAdministrationScopes();
  const key = placeKey(place);
  const known = places[key];
  const confirmed = placeReads[key] !== undefined;
  const [failure, setFailure] = useState<'not-administered' | 'unavailable' | null>(null);
  const [attempt, setAttempt] = useState(0);

  const pair = apiPlace(place);
  const placeKind = pair?.placeKind;
  const placeId = pair?.placeId;

  useEffect(() => {
    setFailure(null);
    if (placeKind === undefined || placeId === undefined || confirmed) {
      return;
    }
    let cancelled = false;
    void readWithRetries(() =>
      apiClient.GET('/v1/administration/{placeKind}/{placeId}', {
        params: { path: { placeKind, placeId } },
      }),
    ).then((outcome) => {
      if (cancelled) {
        return;
      }
      if (outcome === 'unavailable') {
        setFailure('unavailable');
        return;
      }
      if (outcome.data !== undefined && outcome.response.ok) {
        rememberPlace(outcome.data);
        return;
      }
      setFailure(outcome.response.status === 404 ? 'not-administered' : 'unavailable');
    });
    return () => {
      cancelled = true;
    };
  }, [placeKind, placeId, confirmed, attempt, rememberPlace]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  if (place.kind === 'platform') {
    const global = scopes.find((scope) => scope.tier === 'global');
    return {
      state:
        global === undefined
          ? { status: 'not-administered' }
          : { status: 'ready', detail: { place: global.place, tenant: null, can: global.can } },
      retry,
    };
  }
  if (failure !== null && !confirmed) {
    return { state: { status: failure }, retry };
  }
  return {
    state: known === undefined ? { status: 'loading' } : { status: 'ready', detail: known },
    retry,
  };
}
